import { BaseClient } from '../base-client';
import type {
  CreateDepartmentInput,
  Department,
  ExportDepartmentsQuery,
  ImportDepartmentsResult,
  ListDepartmentsQuery,
  PatchDepartmentInput,
} from './types';

type One = { data: Department };
type ListResponse = { data: Department[]; meta: { total: number; limit: number; offset: number } };
type ImportResponse = { data: ImportDepartmentsResult };

export class DepartmentsClient extends BaseClient {
  list(query: ListDepartmentsQuery = {}): Promise<ListResponse> {
    return this.request('/api/departments', { query: normalize(query) });
  }

  /** Batch fetch departments by exactly the referenced ids — bounded,
   *  never capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Department>('/api/departments/find', ids);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/departments/${encodeURIComponent(id)}`);
  }

  create(input: CreateDepartmentInput): Promise<One> {
    return this.request('/api/departments', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchDepartmentInput): Promise<One> {
    return this.request(`/api/departments/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/departments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/departments/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Download the tenant's departments as an XLSX blob. Filter mirrors
   * the list-page's filters exactly (status + search text + sort), so
   * what the user sees on screen is what they get in the file. Pass
   * `{}` to export the whole catalog.
   */
  exportXlsx(
    filter: ExportDepartmentsQuery = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/departments/export', { query: normalize(filter) });
  }

  /**
   * Upload an .xlsx or .csv file. The server upserts by code
   * (case-insensitive); rows that fail validation are returned in the
   * result with their per-row errors so the caller can render a report.
   *
   * Pass `idempotencyKey` to make the request safely retryable — the
   * server stores the response for the key and replays it on retry
   * instead of re-applying the import.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/departments/import', fd, { headers });
  }

  /** Download a blank XLSX template with just the import header row. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/departments/import/template');
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
